import type { AdminStatsDto, NotificationCategory, SignupMethod } from '@rw/shared';
import type { ReactNode } from 'react';
import { LoadingState } from '../../components/states';
import { useAdminStats } from '../../hooks/queries';
import { Link } from 'react-router';
import { formatDateTime, formatNumber, timeAgo } from '../../lib/format';
import { METHOD_LABEL, pct } from './admin-format';
import { useT } from '../../i18n';


const CATEGORY_LABEL: Record<NotificationCategory, string> = {
  social: 'Kudos & comments',
  territory: 'Territory lost',
  clubs: 'Clubs',
  events: 'Events',
  imports: 'Strava imports',
  achievements: 'Ranks & badges',
};

/** Game health for the owner: sign-ups, activity, Strava capacity and the community. */
export function StatsTab() {
  const q = useAdminStats();
  const t = useT();
  if (q.isPending) return <LoadingState />;
  if (!q.data) return <p className="mt-4 text-sm text-ink-500">{t('Stats are not available.')}</p>;
  const s = q.data;
  const { users, activity, strava, stravaApi, push, seen, funnel, community, territory } = s;
  const methods = (Object.keys(METHOD_LABEL) as SignupMethod[]).filter((m) => users.signup[m].total > 0 || m === 'email' || m === 'strava');
  const stravaAccounts = methods.reduce((n, m) => n + users.signup[m].stravaConnected, 0);
  const ofAccounts = (n: number) => t('{pct} of accounts', { pct: pct(n, users.total) });

  return (
    <div className="mt-4 space-y-4" data-testid="admin-stats">
      <Section title="Players">
        <Grid>
          <Stat label="Accounts" value={users.total} testId="stat-users" />
          <Stat label="New · 7 days" value={users.new7d} hint={t('{n} in 30 days', { n: users.new30d })} />
          <Stat label="Online now" value={users.online} />
        </Grid>
        <table className="mt-3 w-full text-sm" data-testid="stat-signup">
          <thead>
            <tr className="text-left text-xs uppercase text-ink-500">
              <th className="pb-1 font-semibold">{t('Signed up with')}</th>
              <th className="pb-1 text-right font-semibold">{t('Accounts')}</th>
              <th className="pb-1 text-right font-semibold">{t('Strava connected')}</th>
            </tr>
          </thead>
          <tbody>
            {methods.map((m) => (
              <tr key={m} className="border-t border-ink-800">
                <td className="py-1.5">{METHOD_LABEL[m]}</td>
                <td className="py-1.5 text-right font-semibold tabular-nums">{users.signup[m].total}</td>
                <td className="py-1.5 text-right tabular-nums">
                  {users.signup[m].stravaConnected}
                  <span className="ml-1 text-xs text-ink-500">{pct(users.signup[m].stravaConnected, users.signup[m].total)}</span>
                </td>
              </tr>
            ))}
            <tr className="border-t border-ink-700 font-semibold">
              <td className="py-1.5">{t('Total')}</td>
              <td className="py-1.5 text-right tabular-nums">{users.total}</td>
              <td className="py-1.5 text-right tabular-nums">
                {stravaAccounts}
                <span className="ml-1 text-xs font-normal text-ink-500">{pct(stravaAccounts, users.total)}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </Section>

      <Section title="Opening the app">
        <Grid>
          <Stat label="Today" value={seen.day1} hint={t('last 24 h')} testId="stat-seen" />
          <Stat label="7 days" value={seen.day7} hint={ofAccounts(seen.day7)} />
          <Stat label="30 days" value={seen.day30} />
        </Grid>
        <p className="mt-2 text-xs text-ink-500">
          {t('{n} accounts have not opened the app in 30 days (tracked since 7 Oct 2026, so early numbers run high).', { n: seen.neverOrLong })}
        </p>
      </Section>

      <Section title="New player funnel">
        <div className="space-y-2" data-testid="stat-funnel">
          <FunnelStep label="Signed up" n={funnel.signedUp} of={funnel.signedUp} />
          <FunnelStep label="First run" n={funnel.firstRun} of={funnel.signedUp} />
          <FunnelStep label="2 runs in the first week" n={funnel.twoRunsFirstWeek} of={funnel.signedUp} />
          <FunnelStep label="Joined a club" n={funnel.joinedClub} of={funnel.signedUp} />
        </div>
        <p className="mt-2 text-xs text-ink-500">{t('All accounts; those younger than a week may still get their second run.')}</p>
      </Section>

      <Section title="Activity">
        <Grid>
          <Stat label="Ran today" value={activity.active1d} hint={t('last 24 h')} />
          <Stat label="Ran · 7 days" value={activity.active7d} hint={ofAccounts(activity.active7d)} />
          <Stat label="Ran · 30 days" value={activity.active30d} />
          <Stat label="Ever ran" value={activity.activated} hint={ofAccounts(activity.activated)} />
          <Stat
            label="Came back"
            value={pct(activity.retained.active, activity.retained.cohort)}
            hint={t('{a}/{b} joined 7–30 d ago, ran this week', { a: activity.retained.active, b: activity.retained.cohort })}
          />
          <Stat label="Flagged · 7 days" value={activity.flagged7d} hint={t('{pct} of runs', { pct: pct(activity.flagged7d, activity.runs7d) })} />
        </Grid>
        <div className="mt-3 text-sm text-ink-300">
          {t('{runs} runs and {km} km in the last 7 days', { runs: formatNumber(activity.runs7d), km: formatNumber(Math.round(activity.distance7dM / 1000)) })}
          {Object.keys(activity.bySource7d).length ? (
            <span className="text-ink-500">
              {' · '}
              {Object.entries(activity.bySource7d)
                .map(([k, n]) => `${k === 'LIVE' ? t('in app') : k.toLowerCase()} ${n}`)
                .join(' · ')}
            </span>
          ) : null}
        </div>
        <Daily days={s.daily} />
      </Section>

      <Section title="Strava">
        <Grid>
          <Stat label="Connected athletes" value={strava.connected} hint={strava.limit ? t('of {n} allowed', { n: strava.limit }) : t('capacity not set')} />
          <Stat label="Auto-import on" value={strava.autoImport} />
          <Stat
            label="Imports · 7 days"
            value={strava.webhooks7d.imported}
            hint={t('{skipped} skipped · {failed} failed', { skipped: strava.webhooks7d.skipped, failed: strava.webhooks7d.failed })}
          />
        </Grid>
        {strava.limit ? <Meter value={strava.connected} max={strava.limit} /> : null}
        <div className="mt-3 space-y-2 text-sm" data-testid="stat-strava-api">
          <div className="text-xs font-semibold uppercase text-ink-400">{t('API calls')}</div>
          {stravaApi.rate ? (
            <>
              <Usage label="15 min" used={stravaApi.rate.usage[0]} limit={stravaApi.rate.limit[0]} />
              <Usage label="Today" used={stravaApi.rate.usage[1]} limit={stravaApi.rate.limit[1]} />
              {stravaApi.rate.readUsage && stravaApi.rate.readLimit ? (
                <>
                  <Usage label="Read · 15 min" used={stravaApi.rate.readUsage[0]} limit={stravaApi.rate.readLimit[0]} />
                  <Usage label="Read · today" used={stravaApi.rate.readUsage[1]} limit={stravaApi.rate.readLimit[1]} />
                </>
              ) : null}
              <p className="text-xs text-ink-500">{t('As counted by Strava, {time}.', { time: timeAgo(stravaApi.rate.at) })}</p>
            </>
          ) : (
            <p className="text-xs text-ink-500">{t("No Strava call since the last deploy, so Strava's own count is not known yet.")}</p>
          )}
          <p className="text-ink-300">
            {t('Our count: {today} today · {week} in 7 days', { today: stravaApi.requestsToday, week: stravaApi.requests7d })}
            {stravaApi.rateLimited7d ? <span className="text-enemy"> · {t('{n} hit the limit', { n: stravaApi.rateLimited7d })}</span> : null}
          </p>
        </div>
        <p className="mt-2 text-xs text-ink-500">
          {t('Last Strava webhook: {time}', { time: strava.lastWebhookAt ? formatDateTime(strava.lastWebhookAt) : t('none yet') })}
        </p>
      </Section>

      <Section title="Clubs">
        {s.clubs.length === 0 ? (
          <p className="text-sm text-ink-500">{t('No clubs yet.')}</p>
        ) : (
          <table className="w-full text-sm" data-testid="stat-clubs">
            <thead>
              <tr className="text-left text-xs uppercase text-ink-500">
                <th className="pb-1 font-semibold">{t('Club')}</th>
                <th className="pb-1 text-right font-semibold">{t('Members')}</th>
                <th className="pb-1 text-right font-semibold">{t('Ran 7d')}</th>
                <th className="pb-1 text-right font-semibold">{t('km 7d')}</th>
                <th className="pb-1 text-right font-semibold">{t('Sectors')}</th>
              </tr>
            </thead>
            <tbody>
              {s.clubs.map((c) => (
                <tr key={c.id} className="border-t border-ink-800">
                  <td className="max-w-0 truncate py-1.5 pr-2">
                    <Link to={`/clubs/${c.id}`} className="font-semibold">
                      <span className="mr-1.5 inline-block size-2 rounded-full" style={{ background: c.color }} />
                      {c.name}
                    </Link>
                  </td>
                  <td className="py-1.5 text-right tabular-nums">{c.members}</td>
                  <td className="py-1.5 text-right tabular-nums">
                    {c.active7d}
                    <span className="ml-1 text-xs text-ink-500">{pct(c.active7d, c.members)}</span>
                  </td>
                  <td className="py-1.5 text-right tabular-nums">{formatNumber(Math.round(c.distance7dM / 1000))}</td>
                  <td className="py-1.5 text-right tabular-nums">{formatNumber(c.sectors)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>

      <Section title="Notifications">
        <Grid>
          <Stat label="Push on" value={users.pushEnabled} hint={ofAccounts(users.pushEnabled)} />
          <Stat label="Sent · 7 days" value={push.sent7d} hint={t('{n} skipped, app was open', { n: push.skippedOnScreen7d })} />
          <Stat
            label="Not delivered"
            value={push.failed7d + push.expired7d}
            hint={t('{failed} failed · {expired} unsubscribed', { failed: push.failed7d, expired: push.expired7d })}
          />
        </Grid>
        <div className="mt-3 text-xs text-ink-400" data-testid="stat-push-off">
          {t('Switched off:')}{' '}
          {Object.keys(push.off).length === 0
            ? t('nobody')
            : (Object.entries(push.off) as [NotificationCategory, number][])
                .sort((a, b) => b[1] - a[1])
                .map(([k, n]) => `${CATEGORY_LABEL[k] ? t(CATEGORY_LABEL[k]) : k} ${n}`)
                .join(' · ')}
        </div>
      </Section>

      <Section title="Community">
        <Grid>
          <Stat label="Clubs" value={community.clubs} />
          <Stat label="In a club" value={users.inClub} hint={ofAccounts(users.inClub)} />
          <Stat label="Live events" value={community.eventsLive} />
          <Stat label="Upcoming events" value={community.eventsUpcoming} />
          <Stat label="Event entries" value={community.eventEntries} hint={t('live + upcoming')} />
          <Stat label="Clubs avg size" value={community.clubs ? Math.round(users.inClub / community.clubs) : 0} />
        </Grid>
      </Section>

      <Section title="Territory">
        <div className="flex items-baseline justify-between text-sm">
          <span>
            <b className="text-lg tabular-nums">{formatNumber(territory.owned)}</b>
            <span className="text-ink-500"> / {t('{n} sectors held', { n: formatNumber(territory.total) })}</span>
          </span>
          <span className="font-semibold">{pct(territory.owned, territory.total)}</span>
        </div>
        <Meter value={territory.owned} max={territory.total} />
      </Section>

      <p className="text-center text-xs text-ink-500">
        {t('Updated {time} · days in {tz}', { time: formatDateTime(s.generatedAt), tz: s.timezone })}
      </p>
    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  const t = useT();
  return (
    <section className="rounded-2xl bg-ink-850 p-4">
      <h2 className="mb-3 font-display text-lg font-extrabold uppercase">{t(title)}</h2>
      {children}
    </section>
  );
}

function Grid({ children }: { children: ReactNode }) {
  return <div className="grid grid-cols-3 gap-2">{children}</div>;
}

function Stat({ label, value, hint, testId }: { label: string; value: number | string; hint?: string; testId?: string }) {
  const t = useT();
  return (
    <div className="min-w-0 rounded-xl bg-ink-900 p-2.5" data-testid={testId}>
      <div className="text-2xl font-extrabold tabular-nums">{typeof value === 'number' ? formatNumber(value) : value}</div>
      <div className="text-[11px] font-semibold uppercase leading-tight text-ink-400">{t(label)}</div>
      {hint ? <div className="mt-0.5 text-[11px] leading-tight text-ink-500">{hint}</div> : null}
    </div>
  );
}

function FunnelStep({ label, n, of }: { label: string; n: number; of: number }) {
  const t = useT();
  return (
    <div>
      <div className="flex justify-between text-sm">
        <span>{t(label)}</span>
        <span className="tabular-nums">
          <b>{formatNumber(n)}</b> <span className="text-xs text-ink-500">{pct(n, of)}</span>
        </span>
      </div>
      <div className="mt-1 h-2 overflow-hidden rounded-full bg-ink-900">
        <div className="h-full rounded-full bg-volt" style={{ width: of > 0 ? `${(n / of) * 100}%` : 0 }} />
      </div>
    </div>
  );
}

function Usage({ label, used, limit }: { label: string; used: number; limit: number }) {
  const t = useT();
  return (
    <div>
      <div className="flex justify-between text-xs">
        <span className="text-ink-400">{t(label)}</span>
        <span className="tabular-nums">
          {formatNumber(used)} / {formatNumber(limit)}
        </span>
      </div>
      <Meter value={used} max={limit} />
    </div>
  );
}

function Meter({ value, max }: { value: number; max: number }) {
  const ratio = max > 0 ? Math.min(1, value / max) : 0;
  return (
    <div className="mt-2 h-2 overflow-hidden rounded-full bg-ink-900">
      <div className={`h-full rounded-full ${ratio > 0.85 ? 'bg-enemy' : 'bg-volt'}`} style={{ width: `${ratio * 100}%` }} />
    </div>
  );
}

/** 30 days of runners (bars) and new accounts (dots). */
function Daily({ days }: { days: AdminStatsDto['daily'] }) {
  const max = Math.max(1, ...days.map((d) => Math.max(d.runners, d.signups)));
  const t = useT();
  return (
    <div className="mt-4" data-testid="stat-daily">
      <div className="mb-1 flex justify-between text-[11px] text-ink-500">
        <span>
          <span className="mr-1 inline-block size-2 rounded-sm bg-volt" />
          {t('runners per day')}
          <span className="ml-3 mr-1 inline-block size-2 rounded-full bg-sky-400" />
          {t('new accounts')}
        </span>
        <span>{t('max {n}', { n: max })}</span>
      </div>
      <div className="flex h-24 items-end gap-[2px]">
        {days.map((d) => (
          <div
            key={d.day}
            className="relative flex h-full flex-1 items-end"
            title={t('{day}: {runners} runners, {runs} runs, {signups} new', { day: d.day, runners: d.runners, runs: d.runs, signups: d.signups })}
          >
            <div className="w-full rounded-t-sm bg-volt/80" style={{ height: `${(d.runners / max) * 100}%` }} />
            {d.signups > 0 ? (
              <div
                className="absolute left-1/2 size-1.5 -translate-x-1/2 rounded-full bg-sky-400"
                style={{ bottom: `calc(${(d.signups / max) * 100}% - 3px)` }}
              />
            ) : null}
          </div>
        ))}
      </div>
      <div className="mt-1 flex justify-between text-[10px] text-ink-500">
        <span>{days[0]?.day.slice(5)}</span>
        <span>{t('today')}</span>
      </div>
    </div>
  );
}
